import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  ExternalLink,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  X,
  Copy,
  Check,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  DollarSign,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  revSyncService,
  Contract,
  ContractDetail,
  ContractFilters,
} from "../../services/RevSyncService";
import { useToastContext } from "../../context/ToastContext";

const Contracts: React.FC = () => {
  const { showToast } = useToastContext();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isItemsPerPageOpen, setIsItemsPerPageOpen] = useState(false);
  const itemsPerPageRef = useRef<HTMLDivElement>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContracts, setTotalContracts] = useState(0);

  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const [isHistoricalFilterOpen, setIsHistoricalFilterOpen] = useState(false);
  const historicalFilterRef = useRef<HTMLDivElement>(null);
  const [isHubspotLinkedFilterOpen, setIsHubspotLinkedFilterOpen] =
    useState(false);
  const hubspotLinkedFilterRef = useRef<HTMLDivElement>(null);

  const [statusFilter, setStatusFilter] = useState<
    "active" | "pending" | "expired" | "cancelled" | "all"
  >("all");
  const [isHistoricalFilter, setIsHistoricalFilter] = useState<boolean | "all">(
    "all"
  );
  const [hubspotLinkedFilter, setHubspotLinkedFilter] = useState<
    boolean | "all"
  >("all");

  const [sortBy, setSortBy] = useState<
    | "createdAt"
    | "updatedAt"
    | "startDate"
    | "endDate"
    | "mrr"
    | "arr"
    | "status"
  >("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [selectedContract, setSelectedContract] =
    useState<ContractDetail | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const slideOutRef = useRef<HTMLDivElement>(null);

  const summaryMetrics = {
    totalMRR: contracts.reduce((sum, c) => sum + c.mrr, 0),
    totalARR: contracts.reduce((sum, c) => sum + c.arr, 0),
  };

  const loadContracts = async (page: number = 1) => {
    try {
      setIsLoading(true);
      const filters: ContractFilters = {
        page,
        limit: itemsPerPage,
      };

      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }

      if (isHistoricalFilter !== "all") {
        filters.isHistorical = isHistoricalFilter === true;
      }

      if (hubspotLinkedFilter !== "all") {
        filters.hubspotLinked = hubspotLinkedFilter === true;
      }

      filters.sortBy = sortBy;
      filters.sortOrder = sortOrder;

      const response = await revSyncService.listContracts(filters);
      setContracts(response.data.contracts);
      setTotalPages(response.data.pagination.totalPages);
      setTotalContracts(response.data.pagination.total);
      setCurrentPage(page);
    } catch (error: any) {
      showToast(error.message || "Failed to load contracts", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContracts(1);
  }, [
    itemsPerPage,
    statusFilter,
    isHistoricalFilter,
    hubspotLinkedFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    if (currentPage !== 1) {
      loadContracts(currentPage);
    }
  }, [currentPage]);

  const handleLoadContractDetail = async (contractId: string) => {
    try {
      setIsLoadingDetail(true);
      const response = await revSyncService.getContract(contractId);
      setSelectedContract(response.data);
      setIsOpening(true);
      setIsDetailModalOpen(true);
      setTimeout(() => {
        setIsOpening(false);
      }, 10);
    } catch (error: any) {
      showToast(error.message || "Failed to load contract details", "error");
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      setIsDetailModalOpen(false);
      setSelectedContract(null);
      setIsClosing(false);
    }, 200);
  };

  useEffect(() => {
    if (isDetailModalOpen && !isClosing) {
      setIsOpening(true);
      setTimeout(() => {
        setIsOpening(false);
      }, 10);
    }
  }, [isDetailModalOpen, isClosing]);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      showToast("success", "Copied to clipboard");
    } catch (error) {
      showToast("error", "Failed to copy");
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-light dark:bg-green-dark/20 text-green-dark dark:text-green-dark";
      case "pending":
        return "bg-secondary/10 text-secondary";
      case "expired":
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300";
      case "cancelled":
        return "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400";
      default:
        return "bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark";
    }
  };

  const getSourceColor = (source: string | null) => {
    switch (source) {
      case "webhook":
        return "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400";
      case "stripe_reconcile":
        return "bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400";
      case "stripe_backfill":
        return "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400";
      case "automated":
        return "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400";
      default:
        return "bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark";
    }
  };

  const getLinkConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case "high":
        return "bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400";
      case "medium":
        return "bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400";
      case "low":
        return "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400";
      case "none":
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300";
      default:
        return "bg-background-panel dark:bg-background-panelDark text-text-secondary dark:text-text-secondaryDark";
    }
  };

  const filteredContracts = contracts.filter((contract) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      contract.customerName?.toLowerCase().includes(query) ||
      contract.customerEmail?.toLowerCase().includes(query) ||
      contract.id.toLowerCase().includes(query) ||
      contract.stripeSubscriptionId?.toLowerCase().includes(query) ||
      contract.stripeCustomerId?.toLowerCase().includes(query) ||
      contract.hubspotDealId?.toLowerCase().includes(query)
    );
  });

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        itemsPerPageRef.current &&
        !itemsPerPageRef.current.contains(event.target as Node)
      ) {
        setIsItemsPerPageOpen(false);
      }
      if (
        statusFilterRef.current &&
        !statusFilterRef.current.contains(event.target as Node)
      ) {
        setIsStatusFilterOpen(false);
      }
      if (
        historicalFilterRef.current &&
        !historicalFilterRef.current.contains(event.target as Node)
      ) {
        setIsHistoricalFilterOpen(false);
      }
      if (
        hubspotLinkedFilterRef.current &&
        !hubspotLinkedFilterRef.current.contains(event.target as Node)
      ) {
        setIsHubspotLinkedFilterOpen(false);
      }
    };

    if (
      isItemsPerPageOpen ||
      isStatusFilterOpen ||
      isHistoricalFilterOpen ||
      isHubspotLinkedFilterOpen
    ) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [
    isItemsPerPageOpen,
    isStatusFilterOpen,
    isHistoricalFilterOpen,
    isHubspotLinkedFilterOpen,
  ]);

  const handleSort = (
    column:
      | "createdAt"
      | "updatedAt"
      | "startDate"
      | "endDate"
      | "mrr"
      | "arr"
      | "status"
  ) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const SortableHeader: React.FC<{
    column:
      | "createdAt"
      | "updatedAt"
      | "startDate"
      | "endDate"
      | "mrr"
      | "arr"
      | "status";
    label: string;
    align?: "left" | "right";
  }> = ({ column, label, align = "left" }) => {
    const isActive = sortBy === column;
    return (
      <th
        className="text-left py-4 px-6 text-xs font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wider cursor-pointer hover:bg-background-panel/30 dark:hover:bg-background-panelDark/30 transition-colors select-none"
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1.5">
          <span>{label}</span>
          {isActive ? (
            sortOrder === "asc" ? (
              <ArrowUp className="w-3 h-3 text-secondary" />
            ) : (
              <ArrowDown className="w-3 h-3 text-secondary" />
            )
          ) : (
            <div className="w-3 h-3" />
          )}
        </div>
      </th>
    );
  };

  return (
    <>
      <div className="w-full h-full bg-background-default dark:bg-background-dark flex flex-col overflow-hidden">
        <div className="max-w-[1600px] mx-auto w-full px-8 py-8 flex flex-col h-full min-h-0">
          {/* Header */}
          <div className="mb-8 flex-shrink-0">
            <h1 className="text-3xl font-semibold text-text-primary dark:text-text-primaryDark mb-2 tracking-tight">
              Contracts
            </h1>
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
              Contracts synced automatically from HubSpot and Stripe
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-5 mb-8 flex-shrink-0">
            <div className="bg-background-paper dark:bg-background-paperDark rounded-xl border border-divider/50 dark:border-divider-dark/50 p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Total MRR
                </div>
                <div className="p-2 bg-background-panel dark:bg-background-panelDark rounded-lg">
                  <TrendingUp className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                </div>
              </div>
              <div className="text-3xl font-bold text-text-primary dark:text-text-primaryDark tracking-tight">
                {formatCurrency(summaryMetrics.totalMRR)}
              </div>
            </div>
            <div className="bg-background-paper dark:bg-background-paperDark rounded-xl border border-divider/50 dark:border-divider-dark/50 p-6 shadow-sm hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark uppercase tracking-wide">
                  Total ARR
                </div>
                <div className="p-2 bg-background-panel dark:bg-background-panelDark rounded-lg">
                  <DollarSign className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                </div>
              </div>
              <div className="text-3xl font-bold text-text-primary dark:text-text-primaryDark tracking-tight">
                {formatCurrency(summaryMetrics.totalARR)}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-2.5 flex-1">
              <div className="relative" ref={statusFilterRef}>
                <button
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
                  className="px-3.5 py-2 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark transition-all duration-150 flex items-center gap-2 min-w-[130px] justify-between shadow-sm hover:shadow"
                >
                  <span>
                    {statusFilter === "all"
                      ? "All Status"
                      : statusFilter.charAt(0).toUpperCase() +
                        statusFilter.slice(1)}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isStatusFilterOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isStatusFilterOpen && (
                  <div className="absolute top-full left-0 mt-1.5 bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg shadow-xl z-50 min-w-[130px] overflow-hidden backdrop-blur-sm">
                    {[
                      { value: "all", label: "All Status" },
                      { value: "active", label: "Active" },
                      { value: "pending", label: "Pending" },
                      { value: "expired", label: "Expired" },
                      { value: "cancelled", label: "Cancelled" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setStatusFilter(option.value as any);
                          setIsStatusFilterOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
                          statusFilter === option.value
                            ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                            : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative" ref={historicalFilterRef}>
                <button
                  onClick={() =>
                    setIsHistoricalFilterOpen(!isHistoricalFilterOpen)
                  }
                  className="px-3.5 py-2 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark transition-all duration-150 flex items-center gap-2 min-w-[130px] justify-between shadow-sm hover:shadow"
                >
                  <span>
                    {isHistoricalFilter === "all"
                      ? "All Historical"
                      : isHistoricalFilter
                      ? "Historical"
                      : "Current"}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isHistoricalFilterOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isHistoricalFilterOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md shadow-lg z-50 min-w-[120px] overflow-hidden">
                    {[
                      { value: "all", label: "All Historical" },
                      { value: "true", label: "Historical" },
                      { value: "false", label: "Current" },
                    ].map((option) => {
                      const isSelected =
                        (option.value === "all" &&
                          isHistoricalFilter === "all") ||
                        (option.value === "true" &&
                          isHistoricalFilter === true) ||
                        (option.value === "false" &&
                          isHistoricalFilter === false);
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                            setIsHistoricalFilter(
                              option.value === "all"
                                ? "all"
                                : option.value === "true"
                            );
                            setIsHistoricalFilterOpen(false);
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
                            isSelected
                              ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                              : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="relative" ref={hubspotLinkedFilterRef}>
                <button
                  onClick={() =>
                    setIsHubspotLinkedFilterOpen(!isHubspotLinkedFilterOpen)
                  }
                  className="px-3.5 py-2 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-divider dark:hover:border-divider-dark transition-all duration-150 flex items-center gap-2 min-w-[130px] justify-between shadow-sm hover:shadow"
                >
                  <span>
                    {hubspotLinkedFilter === "all"
                      ? "All Linked"
                      : hubspotLinkedFilter
                      ? "Linked"
                      : "Not Linked"}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isHubspotLinkedFilterOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isHubspotLinkedFilterOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md shadow-lg z-50 min-w-[120px] overflow-hidden">
                    {[
                      { value: "all", label: "All Linked" },
                      { value: "true", label: "Linked" },
                      { value: "false", label: "Not Linked" },
                    ].map((option) => {
                      const isSelected =
                        (option.value === "all" &&
                          hubspotLinkedFilter === "all") ||
                        (option.value === "true" &&
                          hubspotLinkedFilter === true) ||
                        (option.value === "false" &&
                          hubspotLinkedFilter === false);
                      return (
                        <button
                          key={option.value}
                          onClick={() => {
                            setHubspotLinkedFilter(
                              option.value === "all"
                                ? "all"
                                : option.value === "true"
                            );
                            setIsHubspotLinkedFilterOpen(false);
                            setCurrentPage(1);
                          }}
                          className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors ${
                            isSelected
                              ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                              : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, email, ID, or subscription..."
                    className="w-full pl-11 pr-4 py-2.5 bg-background-paper dark:bg-background-paperDark border border-divider/60 dark:border-divider-dark/60 rounded-lg text-sm text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-text-secondaryDark focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary transition-all duration-150 shadow-sm hover:shadow"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-paper dark:bg-background-paperDark rounded-xl border border-divider/50 dark:border-divider-dark/50 overflow-hidden flex-1 flex flex-col min-h-0 shadow-sm">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-secondary" />
              </div>
            ) : filteredContracts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-8">
                <p className="text-text-secondary dark:text-text-secondaryDark mb-2">
                  No contracts found
                </p>
                <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                  {searchQuery
                    ? "Try adjusting your search or filters"
                    : "Contracts will appear here once the agent is enabled and syncing"}
                </p>
              </div>
            ) : (
              <div className="overflow-auto flex-1">
                <table className="w-full table-auto border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-divider/50 dark:border-divider-dark/50 bg-background-panel/50 dark:bg-background-panelDark/50 backdrop-blur-sm">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wider min-w-[250px]">
                        Customer
                      </th>
                      <th
                        className="text-left py-4 px-6 text-xs font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wider min-w-[180px] cursor-pointer hover:bg-background-panel/30 dark:hover:bg-background-panelDark/30 transition-colors select-none"
                        onClick={() => handleSort("startDate")}
                      >
                        <div className="flex items-center gap-1.5">
                          <span>Dates</span>
                          {sortBy === "startDate" ? (
                            sortOrder === "asc" ? (
                              <ArrowUp className="w-3 h-3 text-secondary" />
                            ) : (
                              <ArrowDown className="w-3 h-3 text-secondary" />
                            )
                          ) : (
                            <div className="w-3 h-3" />
                          )}
                        </div>
                      </th>
                      <SortableHeader column="mrr" label="MRR" />
                      <SortableHeader column="arr" label="ARR" />
                      <SortableHeader column="status" label="Status" />
                      <th className="text-left py-4 px-6 text-xs font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wider">
                        Source
                      </th>
                      <th className="text-left py-4 pl-6 pr-0 text-xs font-semibold text-text-secondary dark:text-text-secondaryDark uppercase tracking-wider">
                        Links
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContracts.map((contract) => (
                      <tr
                        key={contract.id}
                        className="border-b border-divider/30 dark:border-divider-dark/30 hover:bg-background-panel/50 dark:hover:bg-background-panelDark/50 transition-all duration-150 cursor-pointer group"
                        onClick={() => handleLoadContractDetail(contract.id)}
                      >
                        <td className="py-4 px-6 min-w-[250px]">
                          {contract.urls?.stripe?.customer ? (
                            <div className="flex flex-col gap-1">
                              {contract.customerName && (
                                <a
                                  href={contract.urls.stripe.customer}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-block self-start w-fit text-sm font-semibold text-text-primary dark:text-text-primaryDark hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer"
                                >
                                  {contract.customerName}
                                </a>
                              )}
                              {contract.customerEmail && (
                                <a
                                  href={contract.urls.stripe.customer}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-block self-start w-fit text-sm text-text-secondary dark:text-text-secondaryDark hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer"
                                >
                                  {contract.customerEmail}
                                </a>
                              )}
                              {!contract.customerName &&
                                !contract.customerEmail && (
                                  <span className="text-text-secondary dark:text-text-secondaryDark">
                                    —
                                  </span>
                                )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {contract.customerName ? (
                                <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark truncate">
                                  {contract.customerName}
                                </div>
                              ) : null}
                              {contract.customerEmail ? (
                                <div className="text-sm text-text-secondary dark:text-text-secondaryDark truncate">
                                  {contract.customerEmail}
                                </div>
                              ) : null}
                              {!contract.customerName &&
                                !contract.customerEmail && (
                                  <span className="text-text-secondary dark:text-text-secondaryDark">
                                    —
                                  </span>
                                )}
                            </div>
                          )}
                        </td>

                        <td className="py-4 px-6 min-w-[180px]">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark flex-shrink-0 opacity-60" />
                            <div className="flex items-center gap-1.5 min-w-0 flex-1">
                              <span className="text-sm text-text-primary dark:text-text-primaryDark whitespace-nowrap">
                                {formatDate(contract.startDate)}
                              </span>
                              <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                -
                              </span>
                              <span className="text-sm text-text-primary dark:text-text-primaryDark whitespace-nowrap">
                                {formatDate(contract.endDate)}
                              </span>
                            </div>
                          </div>
                          {contract.isHistorical && (
                            <span className="text-xs text-text-secondary dark:text-text-secondaryDark mt-1 block ml-6">
                              Historical
                            </span>
                          )}
                        </td>

                        <td className="py-4 px-6">
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                            {formatCurrency(contract.mrr)}
                          </div>
                        </td>

                        <td className="py-4 px-6">
                          <div className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
                            {formatCurrency(contract.arr)}
                          </div>
                        </td>

                        <td className="py-4 px-6">
                          <span
                            className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize whitespace-nowrap ${getStatusColor(
                              contract.status
                            )}`}
                          >
                            {contract.status}
                          </span>
                        </td>

                        <td className="py-4 px-6">
                          {contract.source ? (
                            <span
                              className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize whitespace-nowrap ${getSourceColor(
                                contract.source
                              )}`}
                            >
                              {contract.source.replace("_", " ")}
                            </span>
                          ) : (
                            <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                              —
                            </span>
                          )}
                        </td>

                        <td
                          className="py-4 pl-6 pr-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {contract.stripeSubscriptionId && (
                              <div className="flex items-center gap-1.5">
                                {contract.urls?.stripe?.subscription ? (
                                  <a
                                    href={contract.urls.stripe.subscription}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium truncate whitespace-nowrap hover:opacity-80 transition-opacity"
                                    style={{
                                      backgroundColor:
                                        "rgba(139, 92, 246, 0.1)",
                                      color: "#8B5CF6",
                                    }}
                                  >
                                    <span>Stripe</span>
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <span
                                    className="px-2.5 py-1 rounded-md text-xs font-medium truncate whitespace-nowrap"
                                    style={{
                                      backgroundColor:
                                        "rgba(139, 92, 246, 0.1)",
                                      color: "#8B5CF6",
                                    }}
                                  >
                                    Stripe
                                  </span>
                                )}
                              </div>
                            )}
                            {contract.urls?.hubspot?.contact && (
                              <div className="flex items-center gap-1.5">
                                <a
                                  href={contract.urls.hubspot.contact}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1.5 px-2.5 py-1 bg-secondary/10 text-secondary rounded-md text-xs font-medium truncate whitespace-nowrap hover:opacity-80 transition-opacity"
                                >
                                  <span>HubSpot</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </div>
                            )}
                            {contract.hubspotDealId &&
                              !contract.urls?.hubspot?.contact && (
                                <div className="flex items-center gap-1.5">
                                  <span className="px-2.5 py-1 bg-secondary/10 text-secondary rounded-md text-xs font-medium truncate whitespace-nowrap">
                                    HubSpot
                                  </span>
                                  {contract.urls?.hubspot?.deal && (
                                    <a
                                      href={contract.urls.hubspot.deal}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="p-1 hover:bg-background-panel dark:hover:bg-background-panelDark rounded transition-colors flex-shrink-0 opacity-60 hover:opacity-100"
                                      aria-label="Open in HubSpot"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5 text-text-secondary dark:text-text-secondaryDark hover:text-secondary" />
                                    </a>
                                  )}
                                </div>
                              )}
                            {!contract.stripeSubscriptionId &&
                              !contract.urls?.hubspot?.contact &&
                              !contract.hubspotDealId && (
                                <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                  —
                                </span>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background-default dark:bg-background-dark border-t border-divider dark:border-divider-dark">
          <div className="max-w-[1600px] mx-auto px-8">
            <div className="flex items-center justify-between gap-4 py-3">
              <div className="relative" ref={itemsPerPageRef}>
                <button
                  onClick={() =>
                    totalContracts > 0 &&
                    setIsItemsPerPageOpen(!isItemsPerPageOpen)
                  }
                  disabled={totalContracts === 0}
                  className="px-3 py-1.5 text-sm font-medium bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark hover:border-text-secondary dark:hover:border-text-secondaryDark transition-colors flex items-center gap-2 min-w-[120px] justify-between disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background-paper dark:disabled:hover:bg-background-paperDark disabled:hover:border-divider dark:disabled:hover:border-divider-dark"
                >
                  <span>{itemsPerPage} per page</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isItemsPerPageOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isItemsPerPageOpen && (
                  <div className="absolute bottom-full left-0 mb-2 bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-md shadow-lg z-50 min-w-[120px] overflow-hidden">
                    {[15, 25, 50, 100, 200].map((value) => (
                      <button
                        key={value}
                        onClick={() => {
                          setItemsPerPage(value);
                          setCurrentPage(1);
                          setIsItemsPerPageOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          itemsPerPage === value
                            ? "bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary font-medium"
                            : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                        }`}
                      >
                        {value} per page
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                  {totalContracts > 0
                    ? `Showing ${
                        (currentPage - 1) * itemsPerPage + 1
                      }-${Math.min(
                        currentPage * itemsPerPage,
                        totalContracts
                      )} of ${totalContracts}`
                    : "No contracts"}
                </span>
                {totalContracts > 0 && (
                  <>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 text-sm font-medium text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Prev</span>
                    </button>

                    {/* Page number buttons */}
                    <div className="flex items-center gap-1.5">
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`min-w-[32px] px-2 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                currentPage === pageNum
                                  ? "bg-secondary text-white dark:bg-secondary dark:text-white"
                                  : "text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 text-sm font-medium text-text-primary dark:text-text-primaryDark hover:bg-background-panel dark:hover:bg-background-panelDark rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-1"
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDetailModalOpen && selectedContract && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md z-50"
            style={{
              opacity: isClosing ? 0 : isOpening ? 0 : 1,
              pointerEvents: isClosing ? "none" : "auto",
              transition: isClosing
                ? "opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
            onClick={handleCloseDetail}
          />

          <div
            ref={slideOutRef}
            className="fixed inset-y-0 right-0 w-full max-w-2xl bg-background-paper dark:bg-background-paperDark shadow-2xl z-50 overflow-y-auto"
            style={{
              transform: isOpening
                ? "translateX(100%)"
                : isClosing
                ? "translateX(100%)"
                : "translateX(0)",
              opacity: isClosing ? 0 : 1,
              transition: isClosing
                ? "transform 0.2s cubic-bezier(0.4, 0, 1, 1), opacity 0.2s cubic-bezier(0.4, 0, 1, 1)"
                : "transform 0.3s cubic-bezier(0, 0, 0.2, 1), opacity 0.3s cubic-bezier(0, 0, 0.2, 1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-6 py-5 border-b border-divider/50 dark:border-divider-dark/50 sticky top-0 bg-background-paper dark:bg-background-paperDark z-10">
                <div>
                  <h2 className="text-xl font-semibold text-text-primary dark:text-text-primaryDark tracking-tight">
                    Contract Details
                  </h2>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        selectedContract.id,
                        `contract-header-${selectedContract.id}`
                      )
                    }
                    className="text-sm text-secondary dark:text-secondary mt-1.5 font-mono hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    {selectedContract.id}
                  </button>
                </div>
                <button
                  onClick={handleCloseDetail}
                  className="p-2 hover:bg-background-panel dark:hover:bg-background-panelDark rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
                </button>
              </div>

              {/* Content - Slack-inspired with generous spacing */}
              <div className="flex-1 px-6 py-6 overflow-y-auto">
                {isLoadingDetail ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-secondary" />
                  </div>
                ) : (
                  <div className="space-y-5 pb-6">
                    {/* Requires Review Alert - Slack-style */}
                    {selectedContract.requiresReview && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200/60 dark:border-yellow-800/60 rounded-xl p-5 flex items-start gap-4">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex-shrink-0">
                          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1.5">
                            Requires Review
                          </h3>
                          <p className="text-sm text-yellow-700 dark:text-yellow-400 leading-relaxed">
                            This contract may need manual review due to data
                            inconsistencies or low link confidence.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Revenue Metrics - Slack-style cards */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-5 border border-divider/50 dark:border-divider-dark/50">
                        <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wide">
                          MRR
                        </div>
                        <div className="text-2xl font-semibold text-text-primary dark:text-text-primaryDark">
                          {formatCurrency(selectedContract.mrr)}
                        </div>
                      </div>
                      <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-5 border border-divider/50 dark:border-divider-dark/50">
                        <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-2 uppercase tracking-wide">
                          ARR
                        </div>
                        <div className="text-2xl font-semibold text-text-primary dark:text-text-primaryDark">
                          {formatCurrency(selectedContract.arr)}
                        </div>
                      </div>
                    </div>

                    {/* Revenue Visualization - Bar Chart & Timeline */}
                    <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-5 border border-divider/50 dark:border-divider-dark/50">
                      <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-4 block uppercase tracking-wide">
                        Revenue Overview
                      </label>

                      {/* MRR vs ARR Bar Chart */}
                      <div className="mb-6">
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={[
                                {
                                  name: "MRR",
                                  value: selectedContract.mrr,
                                  color: "#0077ED",
                                },
                                {
                                  name: "ARR",
                                  value: selectedContract.arr,
                                  color: "#8B5CF6",
                                },
                              ]}
                              margin={{
                                top: 10,
                                right: 10,
                                left: 0,
                                bottom: 10,
                              }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="currentColor"
                                className="stroke-divider dark:stroke-divider-dark opacity-30"
                                vertical={false}
                              />
                              <XAxis
                                dataKey="name"
                                className="text-text-secondary dark:text-text-secondaryDark"
                                axisLine={false}
                                tickLine={false}
                                fontSize={12}
                                fontWeight={500}
                              />
                              <YAxis
                                className="text-text-secondary dark:text-text-secondaryDark"
                                axisLine={false}
                                tickLine={false}
                                fontSize={11}
                                tickFormatter={(value) => {
                                  if (value >= 1000000)
                                    return `$${(value / 1000000).toFixed(1)}M`;
                                  if (value >= 1000)
                                    return `$${(value / 1000).toFixed(0)}K`;
                                  return `$${value}`;
                                }}
                              />
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <div className="bg-background-paper dark:bg-background-paperDark border border-divider dark:border-divider-dark rounded-lg p-2 shadow-lg">
                                        <p className="text-text-primary dark:text-text-primaryDark text-xs font-medium">
                                          {payload[0].name}
                                        </p>
                                        <p className="text-text-primary dark:text-text-primaryDark text-sm font-semibold">
                                          {formatCurrency(
                                            payload[0].value as number
                                          )}
                                        </p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
                              />
                              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                {[
                                  {
                                    name: "MRR",
                                    value: selectedContract.mrr,
                                    color: "#0077ED",
                                  },
                                  {
                                    name: "ARR",
                                    value: selectedContract.arr,
                                    color: "#8B5CF6",
                                  },
                                ].map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {selectedContract.startDate && (
                        <div>
                          <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-3 block uppercase tracking-wide">
                            Contract Timeline
                          </label>
                          {(() => {
                            const startDate = new Date(
                              selectedContract.startDate
                            );
                            const endDate = selectedContract.endDate
                              ? new Date(selectedContract.endDate)
                              : null;
                            const now = new Date();
                            const isActive =
                              selectedContract.status === "active";

                            let progress = 0;
                            let daysElapsed = 0;
                            let totalDays = 0;

                            if (endDate) {
                              totalDays = Math.ceil(
                                (endDate.getTime() - startDate.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );
                              if (
                                isActive &&
                                now >= startDate &&
                                now <= endDate
                              ) {
                                daysElapsed = Math.ceil(
                                  (now.getTime() - startDate.getTime()) /
                                    (1000 * 60 * 60 * 24)
                                );
                                progress = Math.min(
                                  100,
                                  Math.max(0, (daysElapsed / totalDays) * 100)
                                );
                              } else if (now > endDate) {
                                progress = 100;
                                daysElapsed = totalDays;
                              }
                            }

                            const formatShortDate = (date: Date) => {
                              return date.toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year:
                                  date.getFullYear() !== now.getFullYear()
                                    ? "numeric"
                                    : undefined,
                              });
                            };

                            return (
                              <div className="space-y-3">
                                <div className="relative">
                                  <div className="h-2 bg-background-default dark:bg-background-dark rounded-full overflow-hidden">
                                    {endDate && (
                                      <div
                                        className={`h-full rounded-full transition-all ${
                                          isActive && progress < 100
                                            ? "bg-gradient-to-r from-blue-500 to-purple-500"
                                            : progress === 100
                                            ? "bg-green-500"
                                            : "bg-text-secondary dark:bg-text-secondaryDark"
                                        }`}
                                        style={{
                                          width: `${Math.min(
                                            100,
                                            Math.max(0, progress)
                                          )}%`,
                                        }}
                                      />
                                    )}
                                    {!endDate && isActive && (
                                      <div
                                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                                        style={{ width: "100%" }}
                                      />
                                    )}
                                  </div>
                                  {isActive &&
                                    endDate &&
                                    progress < 100 &&
                                    progress > 0 && (
                                      <div
                                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white dark:bg-background-paperDark border-2 border-blue-500 rounded-full shadow-sm"
                                        style={{
                                          left: `${progress}%`,
                                          transform: "translate(-50%, -50%)",
                                        }}
                                      />
                                    )}
                                </div>

                                <div className="flex items-center justify-between text-xs">
                                  <div className="flex flex-col">
                                    <span className="text-text-secondary dark:text-text-secondaryDark font-medium">
                                      Start
                                    </span>
                                    <span className="text-text-primary dark:text-text-primaryDark mt-0.5">
                                      {formatShortDate(startDate)}
                                    </span>
                                  </div>
                                  {isActive &&
                                    endDate &&
                                    progress > 0 &&
                                    progress < 100 && (
                                      <div className="flex flex-col items-center">
                                        <span className="text-text-secondary dark:text-text-secondaryDark font-medium">
                                          {Math.round(progress)}% Complete
                                        </span>
                                        <span className="text-text-primary dark:text-text-primaryDark mt-0.5 text-[10px]">
                                          {daysElapsed} of {totalDays} days
                                        </span>
                                      </div>
                                    )}
                                  {endDate && (
                                    <div className="flex flex-col items-end">
                                      <span className="text-text-secondary dark:text-text-secondaryDark font-medium">
                                        End
                                      </span>
                                      <span className="text-text-primary dark:text-text-primaryDark mt-0.5">
                                        {formatShortDate(endDate)}
                                      </span>
                                    </div>
                                  )}
                                  {!endDate && (
                                    <div className="flex flex-col items-end">
                                      <span className="text-text-secondary dark:text-text-secondaryDark font-medium">
                                        Ongoing
                                      </span>
                                      <span className="text-text-primary dark:text-text-primaryDark mt-0.5 text-[10px]">
                                        No end date
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50">
                        <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-3 block uppercase tracking-wide">
                          Status
                        </label>
                        <span
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize inline-block whitespace-nowrap ${getStatusColor(
                            selectedContract.status
                          )}`}
                        >
                          {selectedContract.status}
                        </span>
                      </div>
                      <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50">
                        <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-3 block uppercase tracking-wide">
                          Link Confidence
                        </label>
                        <span
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize inline-block whitespace-nowrap ${getLinkConfidenceColor(
                            selectedContract.linkConfidence
                          )}`}
                          title={
                            selectedContract.linkConfidence === "high"
                              ? "High: Linked via metadata"
                              : selectedContract.linkConfidence === "medium"
                              ? "Medium: Linked via email match"
                              : selectedContract.linkConfidence === "low"
                              ? "Low: Linked via domain match"
                              : "None: Not linked"
                          }
                        >
                          {selectedContract.linkConfidence}
                        </span>
                      </div>
                    </div>

                    <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-5 border border-divider/50 dark:border-divider-dark/50">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
                          <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-3 block uppercase tracking-wide">
                            Contract Period
                          </label>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                Start:
                              </span>
                              <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                                {formatDate(selectedContract.startDate)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-text-secondary dark:text-text-secondaryDark">
                                End:
                              </span>
                              <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                                {formatDate(selectedContract.endDate)}
                              </span>
                            </div>
                            {selectedContract.isHistorical && (
                              <div className="mt-3 pt-3 border-t border-divider/50 dark:border-divider-dark/50">
                                <span className="text-xs text-text-secondary dark:text-text-secondaryDark italic">
                                  Historical Contract
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-3 block uppercase tracking-wide px-1">
                        Identifiers
                      </label>
                      <div className="space-y-3">
                        <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50 flex items-center justify-between hover:border-divider dark:hover:border-divider-dark transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                              Contract ID
                            </div>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  selectedContract.id,
                                  `contract-${selectedContract.id}`
                                )
                              }
                              className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                            >
                              {selectedContract.id}
                            </button>
                          </div>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                selectedContract.id,
                                `contract-${selectedContract.id}`
                              )
                            }
                            className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors ml-2 flex-shrink-0"
                          >
                            {copiedId === `contract-${selectedContract.id}` ? (
                              <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                            )}
                          </button>
                        </div>

                        {selectedContract.stripeSubscriptionId && (
                          <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50 flex items-center justify-between hover:border-divider dark:hover:border-divider-dark transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                                Stripe Subscription
                              </div>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.stripeSubscriptionId!,
                                    `stripe-${selectedContract.id}`
                                  )
                                }
                                className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                              >
                                {selectedContract.stripeSubscriptionId}
                              </button>
                            </div>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              {selectedContract.urls?.stripe?.subscription && (
                                <a
                                  href={
                                    selectedContract.urls.stripe.subscription
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-[#8B5CF6]" />
                                </a>
                              )}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.stripeSubscriptionId!,
                                    `stripe-${selectedContract.id}`
                                  )
                                }
                                className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                              >
                                {copiedId ===
                                `stripe-${selectedContract.id}` ? (
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedContract.stripeCustomerId && (
                          <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50 flex items-center justify-between hover:border-divider dark:hover:border-divider-dark transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                                Stripe Customer
                              </div>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.stripeCustomerId!,
                                    `stripe-customer-${selectedContract.id}`
                                  )
                                }
                                className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                              >
                                {selectedContract.stripeCustomerId}
                              </button>
                            </div>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              {selectedContract.urls?.stripe?.customer && (
                                <a
                                  href={selectedContract.urls.stripe.customer}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-[#8B5CF6]" />
                                </a>
                              )}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.stripeCustomerId!,
                                    `stripe-customer-${selectedContract.id}`
                                  )
                                }
                                className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                              >
                                {copiedId ===
                                `stripe-customer-${selectedContract.id}` ? (
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedContract.hubspotDealId && (
                          <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50 flex items-center justify-between hover:border-divider dark:hover:border-divider-dark transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                                HubSpot Deal
                              </div>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.hubspotDealId!,
                                    `hubspot-deal-${selectedContract.id}`
                                  )
                                }
                                className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                              >
                                {selectedContract.hubspotDealId}
                              </button>
                            </div>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              {selectedContract.urls?.hubspot?.deal && (
                                <a
                                  href={selectedContract.urls.hubspot.deal}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-secondary" />
                                </a>
                              )}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.hubspotDealId!,
                                    `hubspot-deal-${selectedContract.id}`
                                  )
                                }
                                className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                              >
                                {copiedId ===
                                `hubspot-deal-${selectedContract.id}` ? (
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedContract.hubspotCompanyId && (
                          <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50 flex items-center justify-between hover:border-divider dark:hover:border-divider-dark transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                                HubSpot Company
                              </div>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.hubspotCompanyId!,
                                    `hubspot-company-${selectedContract.id}`
                                  )
                                }
                                className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                              >
                                {selectedContract.hubspotCompanyId}
                              </button>
                            </div>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              {selectedContract.urls?.hubspot?.company && (
                                <a
                                  href={selectedContract.urls.hubspot.company}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-secondary" />
                                </a>
                              )}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.hubspotCompanyId!,
                                    `hubspot-company-${selectedContract.id}`
                                  )
                                }
                                className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                              >
                                {copiedId ===
                                `hubspot-company-${selectedContract.id}` ? (
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedContract.hubspotContactId && (
                          <div className="bg-background-panel dark:bg-background-panelDark rounded-xl p-4 border border-divider/50 dark:border-divider-dark/50 flex items-center justify-between hover:border-divider dark:hover:border-divider-dark transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-text-secondary dark:text-text-secondaryDark mb-1.5 uppercase tracking-wide">
                                HubSpot Contact
                              </div>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.hubspotContactId!,
                                    `hubspot-contact-${selectedContract.id}`
                                  )
                                }
                                className="text-sm font-mono text-text-primary dark:text-text-primaryDark truncate hover:text-secondary dark:hover:text-secondary transition-colors cursor-pointer text-left w-full"
                              >
                                {selectedContract.hubspotContactId}
                              </button>
                            </div>
                            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                              {selectedContract.urls?.hubspot?.contact && (
                                <a
                                  href={selectedContract.urls.hubspot.contact}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                                >
                                  <ExternalLink className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark hover:text-secondary" />
                                </a>
                              )}
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedContract.hubspotContactId!,
                                    `hubspot-contact-${selectedContract.id}`
                                  )
                                }
                                className="p-2 hover:bg-background-default dark:hover:bg-background-dark rounded-lg transition-colors"
                              >
                                {copiedId ===
                                `hubspot-contact-${selectedContract.id}` ? (
                                  <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {!isLoadingDetail && selectedContract && (
                <div className="border-t border-divider/50 dark:border-divider-dark/50 bg-background-paper dark:bg-background-paperDark px-6 py-4 sticky bottom-0">
                  <div className="flex items-center justify-between text-sm flex-wrap gap-3">
                    <div className="flex items-center gap-1">
                      <span className="text-text-secondary dark:text-text-secondaryDark">
                        Created
                      </span>
                      <span className="text-text-primary dark:text-text-primaryDark font-medium">
                        {formatDate(selectedContract.createdAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-text-secondary dark:text-text-secondaryDark">
                        Updated
                      </span>
                      <span className="text-text-primary dark:text-text-primaryDark font-medium">
                        {formatDate(selectedContract.updatedAt)}
                      </span>
                    </div>
                    {selectedContract.source && (
                      <div className="flex items-center gap-1">
                        <span className="text-text-secondary dark:text-text-secondaryDark">
                          Source
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium capitalize whitespace-nowrap ${getSourceColor(
                            selectedContract.source
                          )}`}
                        >
                          {selectedContract.source.replace("_", " ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Contracts;
